import React from 'react';
import { useTranslation } from 'react-i18next';
import DeviceRuleSummary from 'routes/sourceFilters/DeviceRuleSummary';
import { SourceFilter } from 'routes/sourceFilters/sourceFilterTypes';
import { Rule } from 'routes/transformationRules/fieldMappingTypes';
import { ActionPillButton } from 'routes/flowIcons';
import { buttonClasses } from 'styles/tailwindClassnames';
import { FlowDirectionKey } from '../flowMapTypes';

// Field mapping and source filters live on the Route (per flow), not on the
// account, and are edited in the transformation-rules side panel.
// `direction` says which flow's rules these are: "out" = this Route
// (provider → site), "in" = the reverse Route (site → provider), whose rules
// live on that other route record.
// One section per rule kind, selected from the Rules nav — a future geographic
// or time rule is one more nav entry plus its own section here.

const EmptyRule = ({ text, onAdd }: { text: string; onAdd: () => void }) => {
  const { t } = useTranslation();
  return (
    <>
      <p className="mx-auto mt-5 max-w-xl text-center text-body-sm text-ui-text-primary-text">
        {text}
      </p>
      <ActionPillButton label={t('Add Rule')} onClick={onAdd} />
    </>
  );
};

const RuleHeader = ({
  title,
  onEdit,
}: {
  title: string;
  onEdit: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="mb-2 flex items-center gap-3">
      <span className="text-body-sm font-bold text-ui-text-primary-text">
        {title}
      </span>
      <button
        className={`ml-auto ${buttonClasses.inlineLink}`}
        onClick={onEdit}
      >
        {t('Edit')} ▸
      </button>
    </div>
  );
};

export const DeviceFilterSection = ({
  sourceFilter,
  destinationName,
  onOpen,
}: {
  sourceFilter: SourceFilter | null;
  destinationName: string;
  // Straight into the device-filter editor: the user clicked from THIS rule's
  // section, so landing them on the panel's rule list would be a detour.
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  if (!sourceFilter)
    return (
      <EmptyRule
        text={t(
          'Allow or block specific sources on their way to this destination',
        )}
        onAdd={onOpen}
      />
    );
  // The summary reads entirely off the rule: sources_count and providers come
  // back with it, so the card costs no extra request and never needs the device list.
  return (
    <>
      <RuleHeader title={t('Source Filters')} onEdit={onOpen} />
      <div className="rounded border border-ui-divider-lines px-4 py-3 text-body-sm">
        <DeviceRuleSummary
          filter={sourceFilter}
          destinationName={destinationName}
        />
      </div>
    </>
  );
};

export const FieldMappingSection = ({
  rule,
  direction,
  onOpen,
}: {
  rule: Rule | null;
  direction: FlowDirectionKey;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  if (!rule)
    return (
      <EmptyRule
        text={
          direction === 'in'
            ? t('Rules change the data on its way back to this provider')
            : t('Rules change the data on its way to this destination')
        }
        onAdd={onOpen}
      />
    );
  const mapEntries = rule.mapping.map ? Object.entries(rule.mapping.map) : [];
  return (
    <>
      <RuleHeader title={t('Field mapping')} onEdit={onOpen} />
      <div className="rounded border border-ui-divider-lines px-3 py-3 text-body-sm md:px-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="rounded bg-teal-t10 px-2 py-0.5 text-body-sm font-bold text-green-1">
            {rule.streamType === 'ev' ? t('Events') : t('Observations')}
          </span>
          <span className="min-w-0 break-all font-mono text-body-sm text-ui-text-primary-text">
            {rule.mapping.provider_field || t('(source value)')} →{' '}
            {rule.mapping.destination_field}
          </span>
        </div>
        {mapEntries.length > 0 && (
          <div className="flex flex-col gap-1">
            {mapEntries.slice(0, 4).map(([from, to]) => (
              <div
                key={from}
                className="flex flex-wrap items-center gap-2 font-mono text-body-sm text-ui-text-primary-text"
              >
                <span className="break-all rounded border border-ui-divider-lines bg-white px-2 py-0.5">
                  {from}
                </span>
                <span className="font-bold text-green-1">→</span>
                <span className="break-all rounded border border-ui-divider-lines bg-white px-2 py-0.5">
                  {String(to)}
                </span>
              </div>
            ))}
            {mapEntries.length > 4 && (
              <span className="text-body-sm text-ui-text-primary-text">
                {t('+ {{count}} more…', { count: mapEntries.length - 4 })}
              </span>
            )}
          </div>
        )}
        {rule.mapping.default !== undefined && (
          <div className="mt-1 break-all font-mono text-body-sm text-ui-text-primary-text">
            {t('default')}: {String(rule.mapping.default)}
          </div>
        )}
      </div>
    </>
  );
};
